import React from "react";
import {
  TextField,
  Grid2,
  MenuItem,
  InputLabel,
  Select,
  SelectChangeEvent,
  Typography,
  FormControl,
  Button,
  Box,
} from "@mui/material";
import { CommonFormField } from "../../../pages/playground/types";
import CollapsibleFormElement from "../../common/collapsible-form-element";

type GeneralSettingsProps = {
  type: "brand" | "batch";
  fields: CommonFormField[];
  validationErrors: Record<string, string>;
  onChange: (name: string, value: string) => void;
};

const GeneralSettings: React.FC<GeneralSettingsProps> = ({
  type,
  fields,
  validationErrors,
  onChange,
}) => {
  const hasFieldsWithErrors = fields.some(
    (field) => validationErrors[field.key]
  );

  return (
    <CollapsibleFormElement
      title={type === "brand" ? "Brand Settings" : "Batch Settings"}
      hasError={hasFieldsWithErrors}
    >
      <Grid2 container spacing={2}>
        {fields.map((field, index) => (
          <Grid2 size={{ xs: 12, md: 6 }} key={index}>
            {field.type === "text" && (
              <TextField
                fullWidth
                label={field.label}
                value={field.value}
                onChange={(e) => onChange(field.key, e.target.value)}
                error={!!validationErrors[field.key]}
                helperText={
                  validationErrors[field.key] ? validationErrors[field.key] : ""
                }
                required={field.required}
                disabled={field.disabled}
                size="small"
              />
            )}

            {field.type === "select" && (
              <Box>
                {field.showOnlyAction && field.helperAction ? (
                  // Show only the action button without the dropdown
                  <>
                    <Button
                      fullWidth
                      onClick={field.helperAction.onClick}
                      variant="outlined"
                      size="medium"
                      color={validationErrors[field.key] ? "error" : "primary"}
                    >
                      {field.helperAction.text}
                    </Button>
                    {validationErrors[field.key] && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5, ml: 1.5, display: "block" }}
                      >
                        {validationErrors[field.key]}
                      </Typography>
                    )}
                  </>
                ) : (
                  <>
                    <FormControl
                      fullWidth
                      error={!!validationErrors[field.key]}
                      size="small"
                      disabled={field.disabled}
                    >
                      <InputLabel>{field.label}</InputLabel>
                      <Select
                        value={field.value}
                        onChange={(e: SelectChangeEvent) =>
                          onChange(field.key, e.target.value)
                        }
                        label={field.label}
                        disabled={field.disabled}
                      >
                        {field.options?.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {validationErrors[field.key] && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 0.5, ml: 1.5 }}
                        >
                          {validationErrors[field.key]}
                        </Typography>
                      )}
                    </FormControl>
                    {field.helperAction && (
                      <Button
                        size="small"
                        onClick={field.helperAction.onClick}
                        sx={{ mt: 1 }}
                        variant="outlined"
                      >
                        {field.helperAction.text}
                      </Button>
                    )}
                  </>
                )}
              </Box>
            )}
          </Grid2>
        ))}
      </Grid2>
    </CollapsibleFormElement>
  );
};

export default GeneralSettings;
